import React from 'react';
import './CircularProgress.css';

interface CircularProgressProps {
  /** Progress value between 0 and 100 */
  progress: number;
  /** Size of the circular progress in pixels (default: 32) */
  size?: number;
  /** Width of the stroke in pixels (default: 3) */
  strokeWidth?: number;
  /** Color of the progress stroke (default: var(--accentprimary)) */
  color?: string;
  /** Color of the background stroke (default: var(--divider)) */
  backgroundColor?: string;
  /** Whether to show percentage text in the center (default: false) */
  showPercentage?: boolean;
  /** Additional CSS class names */
  className?: string;
  /** Whether to animate progress changes (default: true) */
  animated?: boolean;
}

/**
 * A reusable circular progress component with configurable size, colors, and stroke width.
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <CircularProgress progress={75} />
 * 
 * // Custom size and colors
 * <CircularProgress 
 *   progress={50} 
 *   size={64} 
 *   strokeWidth={4}
 *   color="#3b82f6" 
 *   backgroundColor="#e5e7eb"
 * />
 * 
 * // With percentage text
 * <CircularProgress 
 *   progress={80} 
 *   showPercentage={true}
 *   size={48}
 * />
 * 
 * // Without animation
 * <CircularProgress 
 *   progress={60} 
 *   animated={false}
 * />
 * ```
 */
const CircularProgress: React.FC<CircularProgressProps> = ({
  progress,
  size = 32,
  strokeWidth = 3,
  color = 'var(--accent)',
  backgroundColor = 'var(--divider)',
  showPercentage = false,
  className = '',
  animated = true
}) => {

  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  
  // Ensure progress is clamped between 0 and 100
  const clampedProgress = Math.max(0, Math.min(100, progress));
  
  // Calculate the stroke dash offset to trim the stroke
  const strokeDashoffset = circumference - (clampedProgress / 100) * circumference;



  return (
    <div 
      className={`circular-progress ${className} ${animated ? 'animated' : ''}`} 
      style={{ 
        width: size, 
        height: size
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="circular-progress-svg"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          fill="none"
          className="circular-progress-bg"
        />
        
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="circular-progress-fill"
          style={{
            transform: 'rotate(-90deg)',
            transformOrigin: 'center'
          }}
        />
      </svg>
      
      {showPercentage && (
        <div className="circular-progress-text">
          {Math.round(clampedProgress)}%
        </div>
      )}
    </div>
  );
};

export default CircularProgress;
